import { ZodIssueCode, type ZodErrorMap } from 'zod';
import i18n from './i18n.js';

/**
 * Nothing typed or picked: '' from a text box or a select, a missing value, NaN
 * from an empty number box. Spaces are not blank: a field that allows blank says
 * what is wrong with them instead of calling itself required.
 */
export function isBlank(data: unknown): boolean {
  return data === undefined || data === null || data === '' || Number.isNaN(data);
}

/**
 * zod's default messages are English sentences written for developers ("Invalid
 * uuid", "String must contain at least 1 character(s)"). This answers in the
 * language the operator picked, read when the value is validated.
 *
 * A message a schema sets itself (`.min(1, message)`, `ctx.addIssue({ message })`)
 * still wins: zod consults this map only for issues that carry none. An empty
 * field reads as "required" whatever check it failed — an empty agent select
 * fails `uuid()`, and telling an operator the format is wrong when they have not
 * chosen anything yet is the message this exists to replace.
 */
export const zodErrorMap: ZodErrorMap = (issue, ctx) => {
  const t = (key: string, vars?: Record<string, unknown>) => ({ message: i18n.t(key, vars) });
  if (isBlank(ctx.data) && issue.code !== ZodIssueCode.custom) return t('validation.required');

  switch (issue.code) {
    case ZodIssueCode.invalid_type:
      // `.int()` reports a decimal as an invalid type expecting 'integer'.
      if (issue.expected === 'integer') return t('validation.integer');
      return issue.expected === 'number' || issue.expected === 'bigint'
        ? t('validation.number')
        : t('validation.invalid');
    case ZodIssueCode.invalid_string:
      if (issue.validation === 'email') return t('validation.email');
      if (issue.validation === 'url') return t('validation.url');
      return t('validation.format');
    case ZodIssueCode.too_small: {
      const n = String(issue.minimum);
      if (issue.type === 'string') return t(issue.exact ? 'validation.lengthExact' : 'validation.lengthMin', { n });
      if (issue.type === 'array' || issue.type === 'set') return t('validation.pickMin', { n });
      if (issue.type === 'number' || issue.type === 'bigint') {
        return t(issue.inclusive ? 'validation.numberMin' : 'validation.numberAbove', { n });
      }
      return t('validation.invalid');
    }
    case ZodIssueCode.too_big: {
      const n = String(issue.maximum);
      if (issue.type === 'string') return t(issue.exact ? 'validation.lengthExact' : 'validation.lengthMax', { n });
      if (issue.type === 'array' || issue.type === 'set') return t('validation.pickMax', { n });
      if (issue.type === 'number' || issue.type === 'bigint') {
        return t(issue.inclusive ? 'validation.numberMax' : 'validation.numberBelow', { n });
      }
      return t('validation.invalid');
    }
    case ZodIssueCode.not_multiple_of:
      return t('validation.multipleOf', { n: String(issue.multipleOf) });
    case ZodIssueCode.not_finite:
      return t('validation.number');
    case ZodIssueCode.invalid_date:
      return t('validation.date');
    case ZodIssueCode.invalid_enum_value:
    case ZodIssueCode.invalid_literal:
    case ZodIssueCode.invalid_union:
    case ZodIssueCode.invalid_union_discriminator:
      return t('validation.choice');
    default:
      return t('validation.invalid');
  }
};
